import {cloneDeep} from 'lodash-es';
import {injectAutoEffect} from 'ngxtension/auto-effect';

import {getState, signalStoreFeature, withHooks, withMethods} from '@ngrx/signals';
import {InnerSignalStore} from '@ngrx/signals/src/signal-store-models';

import {SignalStoreDexieIDB} from '../services/dexie-db';
import {getSignalStoreMetaData} from './shared/with-metadata-feature.types';
import {defaultOptions, WithSyncableStateFeatureOptions} from './shared/with-syncable-state-feature.types';
import {patchStateWithAction} from './with-update-state';

/**
 * Adds scale state functionality as a feature to a ngrx `SignalStore`.
 * @template Input - The type of the SignalStore feature's input.
 * @template Output - The type of the SignalStore feature's output.
 * @param options
 * @returns The SignalStore feature with syncable state functionality applied.
 * @depensOn `withMetaData`
 * @NOTE Make sure you use `withMetaData` in your signal store configuration.
 */
export function withSyncableState(options: Partial<WithSyncableStateFeatureOptions> = defaultOptions) {
	const dexieIDB = SignalStoreDexieIDB.getInstance();
	return signalStoreFeature(
		withHooks((store: InnerSignalStore) => ({
			onInit: function () {
				const initialState = cloneDeep(getState(store));
				const storeName = getSignalStoreMetaData(store).StoreName;
				const storeChangeEffect = injectAutoEffect();
				const dbState = dexieIDB.DB_STATE();

				dexieIDB.setSyncableStoreConfig(storeName, {
					lastLoadAt: new Date(),
					clearOn: options.clearOn || 'logout',
				});

				if (Reflect.has(dbState, storeName)) {
					patchStateWithAction(store, dbState[storeName] as any, 'Synced from Dexie IDB');
				} else {
					patchStateWithAction(store, initialState, 'Synced from Dexie IDB (initial state)');
				}

				if (!options.manualSync) {
					storeChangeEffect(() => {
						const storeName = getSignalStoreMetaData(store).StoreName;
						const state = getState(store);
						if (state) {
							const stateToSave = options.simplifyStateFn(state);
							dexieIDB.patchIdbStore(storeName, stateToSave);
						}
					});
				}
			},
		})),
		withMethods((store: InnerSignalStore) => ({
			syncState: function () {
				const storeName = getSignalStoreMetaData(store).StoreName;
				const state = getState(store);
				if (state) {
					const stateToSave = options.simplifyStateFn(state);
					dexieIDB.patchIdbStore(storeName, stateToSave);
				}
			},
			clearStorage: function () {
				const storeName = getSignalStoreMetaData(store).StoreName;
				const clearActionMessage = `[${storeName}] Cleared storage`;
				patchStateWithAction(store, {}, clearActionMessage);
			},
		})),
	);
}
